import { UserConnection } from '../model/UserConnection';
import { ConnectionAction } from './useUserConnection';
import { Dispatch, SyntheticEvent, useCallback } from 'react';
import {
  acceptConnectionRequest,
  cancelConnectionRequest,
  connectPeer,
  disconnectConnection,
  rejectConnectionRequest,
} from '../service/ConnectionService';
import {
  useAnalytics,
  METRICS_COMMUNITY_CONNECTION_CANCEL_PENDING,
  METRICS_COMMUNITY_CONNECTION_REMOVE,
} from '@xolv-mybrightlink/common';

export const useCancelConnectionRequest = (
  connectionDispatch: Dispatch<ConnectionAction>,
  connection: UserConnection,
) => {
  const analytics = useAnalytics();
  return useCallback(
    async (event: SyntheticEvent) => {
      event.stopPropagation();
      console.debug(`Cancel request to peer ${connection.userProfileGuid}`);
      analytics.trackAction(METRICS_COMMUNITY_CONNECTION_CANCEL_PENDING);
      await cancelConnectionRequest(connection.userProfileGuid);
      connectionDispatch({
        type: 'CANCEL_CONNECTION_REQUEST',
        payload: connection,
      });
    },
    [analytics, connectionDispatch, connection],
  );
};

export const useRequestConnect = (
  connectionDispatch: Dispatch<ConnectionAction>,
  connection: UserConnection,
) => {
  return useCallback(
    async (event: SyntheticEvent) => {
      event.stopPropagation();
      console.debug(`Request connect to peer ${connection.userProfileGuid}`);
      await connectPeer(connection.userProfileGuid);
      connectionDispatch({
        type: 'REQUEST_CONNECT',
        payload: connection,
      });
    },
    [connectionDispatch, connection],
  );
};

export const useRejectConnectionRequest = (
  connectionDispatch: Dispatch<ConnectionAction>,
  connection: UserConnection,
) => {
  return useCallback(
    async (event: SyntheticEvent) => {
      event.stopPropagation();
      console.debug(`Cancel request ${connection.guid}`);
      await rejectConnectionRequest(connection.guid);
      connectionDispatch({
        type: 'REJECT_INCOMING_CONNECTION_REQUEST',
        payload: connection,
      });
    },
    [connectionDispatch, connection],
  );
};

export const useAcceptConnectionRequest = (
  connectionDispatch: Dispatch<ConnectionAction>,
  connection: UserConnection,
) => {
  return useCallback(
    async (event: SyntheticEvent) => {
      event.stopPropagation();
      console.debug(`Accept connection request ${connection.guid}`);
      const acceptedConnection = await acceptConnectionRequest(connection.guid);
      if (acceptedConnection) {
        connectionDispatch({
          type: 'ACCEPT_INCOMING_CONNECTION_REQUEST',
          payload: acceptedConnection,
        });
      }
    },
    [connectionDispatch, connection],
  );
};

export const useDisconnectConnection = (
  connectionDispatch: Dispatch<ConnectionAction>,
  connection: UserConnection,
) => {
  const analytics = useAnalytics();
  return useCallback(
    async (event: SyntheticEvent) => {
      event.stopPropagation();
      console.debug(
        `Remove connection from peer ${connection.userProfileGuid}`,
      );
      analytics.trackAction(METRICS_COMMUNITY_CONNECTION_REMOVE);
      await disconnectConnection(connection.userProfileGuid);
      connectionDispatch({
        type: 'DISCONNECT_CONNECTION',
        payload: connection,
      });
    },
    [connectionDispatch, connection, analytics],
  );
};
